import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Typography,
} from "@mui/material";

import { BudgetProfile } from "../../../../pages/budget/modules/components/tabs-content/timeline-tab/timeline-section";
import { formatHoursToTime } from "../../../../utils/formatHoursToTime/formatHoursToTime";

type TTimelineProps = {
    profiles: BudgetProfile[];
    selectedProfile: BudgetProfile | null;
    onSelectProfile: (profile: BudgetProfile) => void;
};

const TimelineChart = ({
    profiles,
    selectedProfile,
    onSelectProfile,
}: TTimelineProps) => {
    const calculateDurationInWeeks = (profile: BudgetProfile) => {
        const daysPerWeek = 5;
        const hoursPerDay =
            profile.hoursPerDay * (profile.efficiencyPercentage / 100);
        const collaborators = (profile.additionalCollaborators ?? 0) + 1;
        const totalWeeklyHours = hoursPerDay * daysPerWeek * collaborators;
        const totalWeeks = Math.ceil(
            profile.totals.totalHours / totalWeeklyHours
        );
        return totalWeeks;
    };

    const formatHoursToShow = (hours: number) => {
        return formatHoursToTime(hours);
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
        }).format(value);
    };

    const calculateTotalHours = () => {
        return formatHoursToShow(
            profiles.reduce((sum, profile) => {
                const profileHours = profile.totals.totalHours;
                return sum + (profileHours || 0);
            }, 0)
        );
    };

    const calculateTotalCost = () => {
        return profiles.reduce((sum, profile) => {
            const profileCost =
                profile.totals.totalPrice *
                (profile.additionalCollaborators + 1);
            return sum + (profileCost || 0);
        }, 0);
    };

    const calculateWeeksSize = () => {
        const weeksSize = profiles.map((profile) => {
            const profileWeeks = calculateDurationInWeeks(profile);
            return profileWeeks;
        });
        const extraWeeks = 3;
        if (weeksSize.length > 0) return Math.max(...weeksSize) + extraWeeks;
        return extraWeeks;
    };

    const calculateTotalWeeks = () => {
        return Math.max(
            ...profiles.map((p) => p.startWeek + calculateDurationInWeeks(p))
        );
    };
    const weeks = Array.from({ length: calculateWeeksSize() }, (_, i) => i + 1);

    return (
        <Paper>
            <TableContainer
                sx={{
                    maxWidth: "75vw",
                }}
            >
                <Table aria-label="timeline table" size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: "15.625rem" }}>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                >
                                    PERFIS DE ATIVIDADE
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ width: "120px" }}>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                >
                                    TOTAL EST.
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ width: "100px" }}>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                >
                                    Nº. COLAB.
                                </Typography>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{ minWidth: "26.25rem" }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                >
                                    SEMANAS
                                </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ width: "150px" }}>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                >
                                    CUSTO TOTAL
                                </Typography>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell colSpan={3} />
                            <TableCell>
                                <div style={{ display: "flex" }}>
                                    {weeks.map((week) => (
                                        <div
                                            key={week}
                                            style={{
                                                width: "2.5rem",
                                                textAlign: "center",
                                                padding: "8px 0",
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {week}
                                            </Typography>
                                        </div>
                                    ))}
                                </div>
                            </TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {profiles.map((profile) => (
                            <TableRow
                                key={profile.id_e}
                                sx={{
                                    bgcolor:
                                        selectedProfile?.id_e === profile.id_e
                                            ? "#f3f6f9"
                                            : "inherit",
                                    "&:hover": {
                                        bgcolor: "#f5f5f5",
                                        cursor: "pointer",
                                    },
                                }}
                                onClick={() => onSelectProfile(profile)}
                            >
                                <TableCell>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Checkbox
                                            checked={
                                                selectedProfile?.id_e ===
                                                profile.id_e
                                            }
                                            onChange={() =>
                                                onSelectProfile(profile)
                                            }
                                            color="primary"
                                        />
                                        <Typography>
                                            {profile.Profile.name}
                                        </Typography>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {formatHoursToShow(
                                            profile.totals.totalHours
                                        )}
                                        hrs
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {profile.additionalCollaborators + 1}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <div
                                        style={{
                                            position: "relative",
                                            height: "40px",
                                        }}
                                    >
                                        <div
                                            style={{
                                                position: "absolute",
                                                left: `${
                                                    (profile.startWeek - 1) * 40
                                                }px`,
                                                width: `${
                                                    calculateDurationInWeeks(
                                                        profile
                                                    ) * 40
                                                }px`,
                                                backgroundColor: "#6E59A5",
                                                opacity: 0.8,
                                                borderRadius: "4px",
                                                marginTop: "12px",
                                                height: "24px",
                                            }}
                                        />
                                    </div>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {formatCurrency(
                                            profile.totals.totalPrice *
                                                (profile.additionalCollaborators +
                                                    1)
                                        )}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}

                        <TableRow>
                            <TableCell>
                                <Typography fontWeight="medium">
                                    Total
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {calculateTotalHours()}hrs
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {profiles.reduce(
                                        (sum, p) =>
                                            sum +
                                            (p.additionalCollaborators + 1),
                                        0
                                    )}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <div
                                    style={{
                                        position: "relative",
                                        height: "40px",
                                    }}
                                >
                                    <div
                                        style={{
                                            position: "absolute",
                                            left: "0px",
                                            width: `${
                                                calculateTotalWeeks() * 40
                                            }px`,
                                            backgroundColor: "#6E59A5",
                                            opacity: 0.7,
                                            borderRadius: "4px",
                                            marginTop: "8px",
                                            height: "24px",
                                        }}
                                    />
                                </div>
                            </TableCell>
                            <TableCell align="right">
                                <Typography
                                    fontWeight="medium"
                                    color="text.secondary"
                                >
                                    {formatCurrency(calculateTotalCost())}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default TimelineChart;
